import { uploadToPresignedUrl, useApi } from '@classgo/app-kit';
import type { ConsentChannel, GrantablePurpose, GuardianItem } from '@classgo/shared';
import { captureConsentInputSchema } from '@classgo/shared';
import { Checkbox, Input, Select } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { peopleApi, peopleQueries } from '../people/api';
import { FormShell } from './form-shell';

const PURPOSES: GrantablePurpose[] = ['general', 'sensitive', 'ai'];

/**
 * Captures consents for a student (ADR-0011): which purposes, through which channel, who
 * authorized (a linked guardian, or a signer's name and document on paper) and, on paper, the
 * scanned form as evidence. The texts the family authorizes are shown with their version.
 */
export const ConsentForm = ({
  studentId,
  guardians,
  defaultPurposes = ['sensitive'],
  onDone,
}: {
  studentId: string;
  guardians: GuardianItem[];
  defaultPurposes?: GrantablePurpose[];
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const texts = useQuery(peopleQueries.consentTexts(api));
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const form = useForm({
    schema: captureConsentInputSchema,
    initial: {
      purposes: defaultPurposes,
      channel: 'app' as ConsentChannel,
      authorizedByUserId:
        guardians.find((guardian) => guardian.isPrimary)?.id ?? guardians[0]?.id ?? '',
      authorizedByName: '',
      authorizedByDocument: '',
      evidenceKey: '',
    },
    toInput: (values) => ({
      purposes: values.purposes,
      channel: values.channel,
      authorizedByUserId:
        values.channel === 'paper' || values.authorizedByUserId === ''
          ? undefined
          : values.authorizedByUserId,
      authorizedByName:
        values.authorizedByName.trim() === '' ? undefined : values.authorizedByName.trim(),
      authorizedByDocument:
        values.authorizedByDocument.trim() === '' ? undefined : values.authorizedByDocument.trim(),
      evidenceKey: values.evidenceKey === '' ? undefined : values.evidenceKey,
    }),
  });
  const togglePurpose = (purpose: GrantablePurpose, checked: boolean) =>
    form.set(
      'purposes',
      checked
        ? [...new Set([...form.values.purposes, purpose])]
        : form.values.purposes.filter((p) => p !== purpose),
    );
  return (
    <FormShell
      error={form.errors.form ?? uploadError}
      busy={form.busy}
      submitLabel={t('consent.submit')}
      onSubmit={(event) => {
        event.preventDefault();
        setUploadError(null);
        void (async () => {
          if (form.values.channel === 'paper' && file && !form.values.evidenceKey) {
            try {
              const presigned = await peopleApi(api).presignAttachment(
                file.name,
                file.type || 'application/pdf',
                file.size,
              );
              await uploadToPresignedUrl({
                url: presigned.url,
                file,
                contentType: file.type || 'application/pdf',
              });
              form.set('evidenceKey', presigned.key);
              await form
                .submit(async () => {
                  await peopleApi(api).captureConsent(
                    studentId,
                    captureConsentInputSchema.parse({
                      purposes: form.values.purposes,
                      channel: 'paper',
                      authorizedByName: form.values.authorizedByName.trim(),
                      authorizedByDocument: form.values.authorizedByDocument.trim() || undefined,
                      evidenceKey: presigned.key,
                    }) as never,
                  );
                })
                .then((ok) => ok && onDone());
            } catch (thrown) {
              setUploadError(thrown instanceof Error ? thrown.message : t('consent.upload.error'));
            }
            return;
          }
          const ok = await form.submit(async (input) => {
            await peopleApi(api).captureConsent(studentId, input as never);
          });
          if (ok) onDone();
        })();
      }}
    >
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-gray-900">{t('consent.purposes')}</legend>
        {PURPOSES.map((purpose) => {
          const text = (texts.data ?? []).find((candidate) => candidate.purpose === purpose);
          return (
            <Checkbox
              key={purpose}
              label={t(`consent.purpose.${purpose}`)}
              description={
                text ? t('consent.textVersion', { title: text.title, version: text.version }) : ''
              }
              checked={form.values.purposes.includes(purpose)}
              onChange={(event) => togglePurpose(purpose, event.target.checked)}
            />
          );
        })}
        {form.fieldError('purposes') ? (
          <p className="text-sm text-danger-700">{form.fieldError('purposes')}</p>
        ) : null}
      </fieldset>
      <Select
        label={t('consent.channel')}
        value={form.values.channel}
        onChange={(event) => form.set('channel', event.target.value as ConsentChannel)}
        options={[
          { value: 'app', label: t('consent.channel.app') },
          { value: 'onboarding', label: t('consent.channel.onboarding') },
          { value: 'paper', label: t('consent.channel.paper') },
        ]}
      />
      {form.values.channel === 'paper' ? (
        <>
          <Input
            label={t('consent.signerName')}
            required
            value={form.values.authorizedByName}
            onChange={(event) => form.set('authorizedByName', event.target.value)}
            {...(form.fieldError('authorizedByName') || form.fieldError('authorizedByUserId')
              ? {
                  error:
                    form.fieldError('authorizedByName') || form.fieldError('authorizedByUserId'),
                }
              : {})}
          />
          <Input
            label={t('consent.signerDocument')}
            value={form.values.authorizedByDocument}
            onChange={(event) => form.set('authorizedByDocument', event.target.value)}
          />
          <Input
            label={t('consent.evidence')}
            help={t('consent.evidence.help')}
            type="file"
            accept="application/pdf,image/*"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            {...(form.fieldError('evidenceKey') ? { error: form.fieldError('evidenceKey') } : {})}
          />
        </>
      ) : (
        <Select
          label={t('consent.authorizedBy')}
          value={form.values.authorizedByUserId}
          onChange={(event) => form.set('authorizedByUserId', event.target.value)}
          options={[
            ...(guardians.length === 0 ? [{ value: '', label: t('consent.noGuardians') }] : []),
            ...guardians.map((guardian) => ({
              value: guardian.id,
              label: `${guardian.givenName ?? ''} ${guardian.familyName ?? ''} · ${guardian.relationship}`,
            })),
          ]}
          {...(form.fieldError('authorizedByUserId')
            ? { error: form.fieldError('authorizedByUserId') }
            : {})}
        />
      )}
    </FormShell>
  );
};
